<template>
  <section>
    <!-- Hero -->
    <base-page-heading :title="$t('admin.keywords.title')">
      <template #extra>
        <b-breadcrumb class="breadcrumb-alt">
          <b-breadcrumb-item>
            <router-link to="/">{{ $t("layout.breadcrumb.home") }}</router-link>
          </b-breadcrumb-item>
          <b-breadcrumb-item>
            {{ $t("layout.breadcrumb.admin") }}
          </b-breadcrumb-item>
          <b-breadcrumb-item active>{{
            $t("admin.keywords.title")
          }}</b-breadcrumb-item>
        </b-breadcrumb>
      </template>
    </base-page-heading>
    <!-- END Hero -->

    <!-- Delete Block Modal -->
    <b-modal id="modal-block-delete" body-class="p-0" hide-footer hide-header>
      <div class="block block-themed block-transparent mb-0">
        <div class="block-header bg-primary-dark">
          <h3 class="block-title">
            {{ $t("admin.keywords.modal.delete.title") }}
          </h3>
          <div class="block-options">
            <button
              type="button"
              class="btn-block-option"
              @click="$bvModal.hide('modal-block-delete')"
            >
              <i class="fa fa-fw fa-times"></i>
            </button>
          </div>
        </div>
        <div class="block-content font-size-sm">
          <p>
            {{
              $t("admin.keywords.modal.delete.msg", {
                keyword: dropKeyword.keyword
              })
            }}
          </p>
        </div>
        <div class="block-content block-content-full text-right border-top">
          <b-button
            variant="light"
            size="sm"
            @click="$bvModal.hide('modal-block-delete')"
            >{{ $t("admin.keywords.modal.delete.no") }}</b-button
          >
          <b-button
            variant="primary"
            size="sm"
            @click="removeKeyword(dropKeyword.id)"
          >
            <i class="fa fa-check mr-1"></i>
            {{ $t("admin.keywords.modal.delete.yes") }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <!-- END Delete Block Modal -->

    <div class="content content-narrow">
      <b-row>
        <b-col>
          <base-block
            :title="$t('admin.keywords.table.title')"
            header-bg
            content-full
          >
            <b-table-simple
              striped
              hover
              borderless
              class="table-vcenter font-size-sm mb-0"
            >
              <b-thead>
                <b-tr>
                  <b-th class="font-w700">{{
                    $t("admin.keywords.table.keyword")
                  }}</b-th>
                  <b-th
                    class="d-sm-table-cell font-w700"
                    v-if="Auth.isUserManager()"
                    >{{ $t("admin.keywords.table.action") }}</b-th
                  >
                </b-tr>
              </b-thead>
              <b-tfoot v-if="Auth.isUserManager()">
                <b-tr>
                  <b-th class="font-w700">
                    <b-input
                      name="newKeyword"
                      type="text"
                      v-model="newKeyword"
                      class="form-control"
                      @keyup.enter="addKeyword"
                    />
                  </b-th>
                  <b-th class="d-sm-table-cell font-w700">
                    <button
                      class="btn btn-primary btn-block"
                      @click="addKeyword"
                    >
                      {{ $t("admin.keywords.table.button.add") }}
                    </button>
                  </b-th>
                </b-tr>
              </b-tfoot>
              <b-tbody>
                <b-tr v-for="(keyword, index) in keywords" :key="keyword.id">
                  <b-td class="text-left">
                    <span v-if="editKeyword !== index">
                      {{ keyword.keyword }}
                    </span>
                    <b-input-group v-else>
                      <b-form-input
                        type="text"
                        v-model="keywords[index].keyword"
                        @keyup.enter="saveKeyword"
                      ></b-form-input>
                      <b-input-group-append>
                        <b-button
                          size="sm"
                          variant="alt-primary"
                          @click="saveKeyword"
                        >
                          <i class="fa fa-fw fa-save"></i>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-td>
                  <b-td class="d-sm-table-cell" v-if="Auth.isUserManager()">
                    <template v-if="editKeyword !== index">
                      <b-button
                        size="sm"
                        variant="alt-primary"
                        @click="editKeyword = index"
                      >
                        <i class="fa fa-fw fa-pencil-alt"></i>
                      </b-button>
                      <b-button
                        size="sm"
                        class="button-margin"
                        variant="alt-danger"
                        v-b-modal.modal-block-delete
                        @click="dropKeyword = keyword"
                      >
                        <i class="fa fa-fw fa-times"></i>
                      </b-button>
                    </template>
                    <b-button
                      v-else
                      size="sm"
                      variant="alt-primary"
                      @click="editKeyword = null"
                    >
                      <i class="fa fa-fw fa-window-close"></i>
                    </b-button>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </base-block>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
// @ is an alias to /src
import Keywords from "@/assets/js/Keywords";
import Loading from "@/store/loading";
import Notification from "@/assets/js/Notification";
import Auth from "@/assets/js/Auth";

import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";

export default {
  name: "Keywords",
  components: {},
  mixins: [validationMixin],
  data() {
    return {
      newKeyword: "",
      editKeyword: null,
      dropKeyword: {
        id: null,
        keyword: ""
      },
      keywords: [],
      Auth: Auth
    };
  },
  validations: {
    newKeyword: {
      required,
      minLength: minLength(3)
    }
  },
  mounted() {
    document.title = this.$t("admin.keywords.title");

    Notification.init(this);

    this.getKeywordList();
  },
  updated() {},
  methods: {
    saveKeyword: function() {
      let keyword = this.keywords[this.editKeyword];

      if (keyword.keyword === "" || keyword.keyword.length < 3) {
        Notification.push(
          "",
          this.$t("admin.keywords.notifications.error.notChanged"),
          "danger"
        );
      } else {
        Loading.dispatch("increase");
        Keywords.updateKeyword(keyword.id, keyword.keyword).then(result => {
          if (result === false) {
            Notification.push(
              "",
              this.$t("admin.keywords.notifications.error.notChanged"),
              "danger"
            );
            Loading.dispatch("reduce");
          } else {
            Notification.push(
              "",
              this.$t("admin.keywords.notifications.error.changed"),
              "success"
            );
            this.editKeyword = null;

            Loading.dispatch("reduce");
          }
        });
      }
    },
    removeKeyword: function(keywordId) {
      this.$bvModal.hide("modal-block-delete");

      Loading.dispatch("increase");
      Keywords.removeKeyword(keywordId).then(result => {
        if (result === false) {
          Notification.push(
            "",
            this.$t("admin.keywords.notifications.error.notDeleted"),
            "danger"
          );
          Loading.dispatch("reduce");
        } else {
          Notification.push(
            "",
            this.$t("admin.keywords.notifications.error.deleted"),
            "success"
          );
          this.getKeywordList();

          Loading.dispatch("reduce");
        }
      });
    },
    addKeyword: function() {
      this.$v.newKeyword.$touch();

      if (this.$v.newKeyword.$anyError) {
        Notification.push(
          "",
          this.$t("admin.keywords.notifications.error.notAdded"),
          "danger"
        );
      } else {
        Loading.dispatch("increase");
        Keywords.addKeyword(this.newKeyword).then(result => {
          if (result == null) {
            Notification.push(
              "",
              this.$t("admin.keywords.notifications.error.notAdded"),
              "danger"
            );
            Loading.dispatch("reduce");
          } else {
            Notification.push(
              "",
              this.$t("admin.keywords.notifications.error.added"),
              "success"
            );
            this.getKeywordList();
            this.newKeyword = "";

            Loading.dispatch("reduce");
          }
        });
      }
    },
    getKeywordList: function() {
      Loading.dispatch("increase");
      Keywords.getList().then(keywordList => {
        this.keywords = keywordList;
        Loading.dispatch("reduce");
      });
    }
  }
};
</script>

<style lang="scss">
.button-margin {
  margin-left: 1em;
}
</style>
